// Um cartão por vínculo em "Meu cadastro" (Parte 6.7 "um cartão por pessoa jurídica"; REQ-PPL-002, 006, 007, 015): a pessoa jurídica
// (centro ou holding), o tipo, a função, a situação e a vigência — e, SÓ no vínculo vigente e flexível (`PJ`/`CooperadoCPESQ`),
// a disponibilidade que a própria pessoa declara. Vínculo fixo (`Socio`/`CLT`/`Estagiario`) tem escala de trabalho definida pela
// coordenação: a tela não mostra campo de jornada de ninguém (regra 9 da Parte 0.3) e vínculo flexível nunca mostra escala.
// Vínculo agendado, vencido ou encerrado é só leitura: não se declara disponibilidade num vínculo que não está vigente.
import { Badge } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { useMe } from '../../../lib/auth/authContext';
import type { PersonLink } from '../api/queries';
import { useCivilDate } from '../lib/civilDateDisplay';
import { scheduleModeFor } from '../lib/scheduleMode';
import { MyAvailabilityPanel } from './MyAvailabilityPanel';

export function MyLinkCard({ link }: { readonly link: PersonLink }) {
  const { t } = useTranslation('people');
  const civil = useCivilDate();
  const me = useMe();
  const context = link.tenantId
    ? me.contexts.find((item) => item.tenantId === link.tenantId)
    : undefined;
  const entity = context
    ? context.name
    : link.tenantId === null
      ? t('me.links.holding')
      : t('me.links.otherCenter');
  const mode = scheduleModeFor(link.linkType);
  const active = link.status === 'Active';
  return (
    <article
      className="flex flex-col gap-3 rounded-card border p-4"
      aria-labelledby={`my-link-${link.id}-title`}
      data-testid={`my-link-${link.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id={`my-link-${link.id}-title`} className="text-body font-semibold">
            {entity}
          </h3>
          <p className="text-body text-muted-foreground">
            <span className="font-mono text-caption">{link.functionCode}</span>
            {' · '}
            {civil(link.effectiveFrom)}
            {' → '}
            {link.effectiveTo ? civil(link.effectiveTo) : t('me.links.openEnded')}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline">
            {t(`common.linkType.${link.linkType}`, { defaultValue: link.linkType })}
          </Badge>
          <Badge
            variant={active ? 'success' : 'secondary'}
            data-testid={`my-link-status-${link.id}`}
          >
            {t(`common.linkStatus.${link.status}`, { defaultValue: link.status })}
          </Badge>
        </div>
      </div>
      {!active ? (
        <p className="text-body text-muted-foreground" data-testid={`my-link-inactive-${link.id}`}>
          {t('me.links.inactive')}
        </p>
      ) : mode === 'flexible' ? (
        <MyAvailabilityPanel linkId={link.id} />
      ) : (
        <p className="text-body text-muted-foreground" data-testid={`my-link-fixed-${link.id}`}>
          {t('me.links.fixed')}
        </p>
      )}
    </article>
  );
}
